import clsx from "clsx";
import { useCallback, useId, useMemo, useState } from "react";

import { FormControl } from "./FormControl";

const submitFormOnCmdEnter: React.KeyboardEventHandler<HTMLTextAreaElement> = (
  event,
) => {
  if (event.key === "Enter" && event.metaKey) {
    const form = event.currentTarget.closest("form");
    if (form && form.querySelector("button[type=submit]")) {
      // submit the form if it has a submit button
      form.requestSubmit();
    }
  }
};

export const Textarea: React.FC<
  Pick<React.ComponentProps<typeof FormControl>, "label" | "error" | "hint"> &
    React.ComponentProps<"textarea">
> = ({
  label,
  error,
  hint,
  className,
  defaultValue = "",
  value,
  onChange,
  onKeyDown = submitFormOnCmdEnter,
  ...props
}) => {
  const id = useId();
  const [internalValue, setInternalValue] = useState(defaultValue.toString());

  const handleChange = useCallback<
    React.ChangeEventHandler<HTMLTextAreaElement>
  >((event) => {
    setInternalValue(event.target.value);
  }, []);

  const displayValue = useMemo(
    () => value?.toString() ?? internalValue,
    [value, internalValue],
  );

  return (
    <FormControl id={id} label={label} error={error} hint={hint}>
      <div className="relative grid">
        <textarea
          id={id}
          className={clsx(
            "textarea textarea-bordered resize-none overflow-hidden [grid-area:1/1/2/2]",
            error && "textarea-error",
            className,
          )}
          value={displayValue}
          onChange={onChange ?? handleChange}
          onKeyDown={onKeyDown}
          {...props}
        />
        {/**
         * we don't need aria-hidden here because it's already invisible
         * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-hidden#:~:text=aria%2Dhidden%3D%22true%22%20should%20not%20be%20added,The%20HTML%20hidden%20attribute%20is%20present
         */}
        <div className="textarea textarea-bordered invisible whitespace-pre-wrap [grid-area:1/1/2/2]">
          {displayValue}
          {
            // add a space if the last character is a newline
            /[\r\n]$/.test(displayValue) && " "
          }
        </div>
        {props.maxLength && displayValue.length >= props.maxLength / 2 && (
          <div className="absolute bottom-1.5 right-1.5 text-xs leading-none">
            {displayValue.length}/{props.maxLength}
          </div>
        )}
      </div>
    </FormControl>
  );
};
